import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import RecordingUI from '../components/RecordingUI';
import LoadingScreen from '../components/LoadingScreen';
import { transcribeAudio, structureTranscript } from '../utils/api';
import { saveToHistory } from '../utils/history';
import Title from '../assets/Title.png';
import './Record.css';

const Record = () => {
  const navigate = useNavigate();
  const [isProcessing, setIsProcessing] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('Unscrambling your thoughts...');
  const [error, setError] = useState(null);

  const handleRecordingComplete = async (audioBlob) => {
    setIsProcessing(true);
    setError(null);

    try {
      // Step 1: Transcribe
      setLoadingMessage('Transcribing your audio...');
      const transcribeResult = await transcribeAudio(audioBlob);

      if (!transcribeResult.success) {
        throw new Error(transcribeResult.error || 'Transcription failed');
      }

      // Step 2: Structure
      setLoadingMessage('Structuring your thoughts...');
      const structureResult = await structureTranscript(transcribeResult.transcript);

      if (!structureResult.success) {
        throw new Error(structureResult.error || 'Structuring failed');
      }

      // Step 3: Save to history
      const historyEntry = saveToHistory({
        transcript: structureResult.transcript,
        brief: structureResult.outputs.brief,
        detailed: structureResult.outputs.detailed,
        detectedContext: structureResult.detectedContext
      });

      console.log('History entry saved:', historyEntry);

      // Navigate to results
      const resultId = historyEntry?.id || Date.now().toString();
      navigate(`/results/${resultId}`, {
        state: {
          transcript: structureResult.transcript,
          brief: structureResult.outputs.brief,
          detailed: structureResult.outputs.detailed,
          detectedContext: structureResult.detectedContext,
          isNew: true
        }
      });

    } catch (err) {
      console.error('Processing error:', err);
      setError(err.message || 'Something went wrong. Please try again.');
      setIsProcessing(false);
    }
  };

  const handleError = (errorMessage) => {
    setError(errorMessage);
  };

  return (
    <motion.div
      className="page record-page"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
    >
      <AnimatePresence>
        {isProcessing && <LoadingScreen message={loadingMessage} />}
      </AnimatePresence>

      <header className="header">
        <Link to="/" className="logo">
          <img src={Title} alt="Unscramble Logo" className="logo-image" />
        </Link>
      </header>

      <main className="record-main">
        <motion.h2
          className="record-title"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          Let it all out
        </motion.h2>

        <RecordingUI
          onRecordingComplete={handleRecordingComplete}
          onError={handleError}
        />

        <AnimatePresence>
          {error && (
            <motion.div
              className="error-message"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 20 }}
            >
              <p>{error}</p>
              <button
                className="btn btn-secondary retry-btn"
                onClick={() => setError(null)}
              >
                Try Again
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </main>
    </motion.div>
  );
};

export default Record;
